"use client";

import { createContext, useContext, useEffect, useState, useCallback } from "react";
import fr from "./fr.json";
import en from "./en.json";
import ar from "./ar.json";

type Dict = typeof fr;
const DICTS: Record<string, Dict> = { fr, en, ar };
const RTL = new Set(["ar"]);
export const LANGS = [
  { code: "fr", label: "Français" },
  { code: "en", label: "English" },
  { code: "ar", label: "العربية" },
];

type Ctx = { lang: string; setLang: (l: string) => void; t: (key: string) => string; dir: "ltr" | "rtl" };
const I18nCtx = createContext<Ctx>({ lang: "fr", setLang: () => {}, t: (k) => k, dir: "ltr" });

function resolve(dict: Dict, key: string): string {
  const parts = key.split(".");
  let cur: unknown = dict;
  for (const p of parts) {
    if (cur && typeof cur === "object" && p in (cur as Record<string, unknown>)) {
      cur = (cur as Record<string, unknown>)[p];
    } else return key;
  }
  return typeof cur === "string" ? cur : key;
}

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [lang, setLangState] = useState("fr");

  useEffect(() => {
    const saved = typeof window !== "undefined" ? localStorage.getItem("lang") : null;
    if (saved && DICTS[saved]) setLangState(saved);
  }, []);

  useEffect(() => {
    const dir = RTL.has(lang) ? "rtl" : "ltr";
    if (typeof document !== "undefined") {
      document.documentElement.lang = lang;
      document.documentElement.dir = dir;
    }
  }, [lang]);

  const setLang = useCallback((l: string) => {
    if (!DICTS[l]) return;
    setLangState(l);
    if (typeof window !== "undefined") localStorage.setItem("lang", l);
  }, []);

  const t = useCallback((key: string) => resolve(DICTS[lang] ?? fr, key), [lang]);
  const dir: "ltr" | "rtl" = RTL.has(lang) ? "rtl" : "ltr";

  return <I18nCtx.Provider value={{ lang, setLang, t, dir }}>{children}</I18nCtx.Provider>;
}

export const useI18n = () => useContext(I18nCtx);
